import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/auth-context';
import { useResume } from '@/contexts/resume-context';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Briefcase, Loader2, FileText } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/lib/supabase';

const formSchema = z.object({
  firstName: z.string()
    .min(2, { message: 'First name must be at least 2 characters' })
    .max(50, { message: 'First name must be less than 50 characters' })
    .regex(/^[a-zA-Z\s'-]+$/, { message: 'First name can only contain letters, spaces, hyphens and apostrophes' }),
  lastName: z.string()
    .min(2, { message: 'Last name must be at least 2 characters' })
    .max(50, { message: 'Last name must be less than 50 characters' })
    .regex(/^[a-zA-Z\s'-]+$/, { message: 'Last name can only contain letters, spaces, hyphens and apostrophes' }),
  email: z.string()
    .email({ message: 'Please enter a valid email address' })
    .min(5, { message: 'Email must be at least 5 characters' })
    .max(254, { message: 'Email must be less than 254 characters' }),
  password: z.string()
    .min(8, { message: 'Password must be at least 8 characters' })
    .max(72, { message: 'Password must be less than 72 characters' })
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, { 
      message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number' 
    }),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type FormValues = z.infer<typeof formSchema>;

export default function RegisterPage() {
  const { signUp } = useAuth();
  const { parsedResume, hasPendingResume } = useResume();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if we have a pending resume from the query params
  const queryParams = new URLSearchParams(location.search);
  const hasPendingResumeParam = queryParams.get('resume') === 'pending';

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: parsedResume?.personalInfo?.fullName?.split(' ')[0] || '',
      lastName: parsedResume?.personalInfo?.fullName?.split(' ').slice(1).join(' ') || '',
      email: parsedResume?.personalInfo?.email || '',
      password: '',
      confirmPassword: '',
    },
  });

  // Update form values when parsedResume changes
  useEffect(() => {
    if (parsedResume?.personalInfo) {
      const nameParts = parsedResume.personalInfo.fullName?.split(' ') || [];
      form.setValue('firstName', nameParts[0] || '');
      form.setValue('lastName', nameParts.slice(1).join(' ') || '');
      form.setValue('email', parsedResume.personalInfo.email || '');
    }
  }, [parsedResume, form]);

  const onSubmit = async (data: FormValues) => {
    if (isLoading) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // First check if user exists
      const { data: existingUser } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password
      });

      if (existingUser?.user) {
        setError('An account with this email already exists. Please sign in instead.');
        navigate('/login');
        return;
      }

      // Proceed with signup
      const { error } = await signUp(data.email, data.password, data.firstName, data.lastName);
      
      if (error) {
        if (error.message?.includes('valid email')) {
          setError('Please enter a valid email address.');
        } else if (error.message?.includes('password')) {
          setError('Password is invalid. Please check the requirements and try again.');
        } else if (error.message?.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please try again.');
        } else if (error.message?.includes('Email not confirmed')) {
          setError('Please check your email and verify your account before signing in.');
        } else if (error.message?.includes('timed out')) {
          setError('Login attempt timed out. Please try again.');
        } else {
          setError(error.message || 'Failed to create account. Please try again.');
        }
        
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error.message || 'Failed to create account',
        });
      } else {
        // Create initial profile
        const { error: profileError } = await supabase
          .from('profiles')
          .insert([{
            id: (await supabase.auth.getUser()).data.user?.id,
            first_name: data.firstName,
            last_name: data.lastName,
            email: data.email,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            job_preferences: {
              cultureFit: 3,
              compensation: 3,
              workLifeBalance: 3,
              location: 3,
              role: 3,
              stability: 3,
              skills: 3,
              industry: 3,
              leadership: 3,
              growth: 3
            },
            notification_preferences: {
              messages: true,
              connections: true,
              opportunities: true,
              applications: true,
              interviews: true,
              profile_views: true,
              content_engagement: true,
              company_updates: true,
              team_activity: true,
              product_updates: true,
              marketing_emails: true,
              digest_emails: true,
              email_notifications: true,
              push_notifications: true
            }
          }]);

        if (profileError) {
          console.error('Error creating profile:', profileError);
        }

        toast({
          title: 'Account Created',
          description: 'Welcome to NewLoop! Please check your email to verify your account.',
        });

        // If we have a pending resume, navigate to onboarding with resume param
        if (hasPendingResume || hasPendingResumeParam) {
          navigate('/onboarding?resume=pending');
        } else {
          navigate('/onboarding');
        }
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      setError('An unexpected error occurred. Please try again.');
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center py-8">
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px] px-4">
        <div className="flex flex-col space-y-2 text-center">
          <div className="flex justify-center">
            <div className="rounded-full bg-primary/10 p-3">
              <Briefcase className="h-8 w-8 text-primary" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Create an account</h1>
          <p className="text-sm text-muted-foreground">
            Enter your information to create an account
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {(hasPendingResume || hasPendingResumeParam) && (
          <Alert>
            <FileText className="h-4 w-4" />
            <AlertDescription>
              We've prefilled some information from your resume. Please verify and complete your registration.
            </AlertDescription>
          </Alert>
        )}

        <div className="bg-card border rounded-lg p-6 shadow-sm">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="John" 
                          {...field} 
                          autoComplete="given-name"
                          className={parsedResume?.personalInfo?.fullName ? "border-green-500 bg-green-50" : ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Doe" 
                          {...field} 
                          autoComplete="family-name"
                          className={parsedResume?.personalInfo?.fullName ? "border-green-500 bg-green-50" : ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="name@example.com" 
                        type="email"
                        autoComplete="email"
                        {...field} 
                        className={parsedResume?.personalInfo?.email ? "border-green-500 bg-green-50" : ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="bg-primary/5 p-4 rounded-lg space-y-4 border-2 border-primary/20">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input 
                          type="password" 
                          placeholder="••••••••"
                          autoComplete="new-password"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm Password</FormLabel>
                      <FormControl>
                        <Input 
                          type="password" 
                          placeholder="••••••••"
                          autoComplete="new-password"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
          </Form>
        </div>
        <div className="text-center text-sm">
          Already have an account?{' '}
          <Link to="/login" className="text-primary hover:underline underline-offset-4">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}